/**
 * React Query hooks for taxonomy schema management
 * Provides type-safe data fetching and mutations for taxonomy schemas
 */

import {
  useQuery,
  useQueries,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';

import { projectsKeys } from '@/lib/queryClient';
import {
  getTaxonomySchema,
  saveTaxonomySchema,
  deleteTaxonomySchema,
  getTaxonomyAnalytics,
} from '@/services/api';
import type {
  TaxonomyField,
  TaxonomySchema,
  SaveTaxonomySchemaRequest,
  TaxonomyAnalyticsResponse,
} from '@/types/taxonomy';

// ============================================================================
// QUERY KEYS
// ============================================================================

export const taxonomyKeys = {
  all: ['taxonomy'] as const,
  schema: (projectId: string) => [...taxonomyKeys.all, 'schema', projectId] as const,
  analytics: (projectId: string) => [...taxonomyKeys.all, 'analytics', projectId] as const,
};

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Fetch the taxonomy schema for a project
 */
export function useTaxonomySchema(
  projectId: string
): UseQueryResult<TaxonomySchema | null, Error> {
  return useQuery({
    queryKey: taxonomyKeys.schema(projectId),
    queryFn: async () => {
      const schema = await getTaxonomySchema(projectId);
      return schema;
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Fetch aggregated taxonomy analytics for a project
 */
export function useTaxonomyAnalytics(
  projectId: string,
  enabled: boolean = true
): UseQueryResult<TaxonomyAnalyticsResponse, Error> {
  return useQuery({
    queryKey: taxonomyKeys.analytics(projectId),
    queryFn: async () => {
      const analytics = await getTaxonomyAnalytics(projectId);
      return analytics;
    },
    enabled: !!projectId && enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes - analytics change more frequently
  });
}

/**
 * Fetch taxonomy schemas for multiple projects and build unified label maps.
 * Used for multi-field mode where we need labels from all projects.
 * T121h: Enables human-readable field/option names across all projects.
 */
export function useAllProjectTaxonomySchemas(projectIds: string[]) {
  const results = useQueries({
    queries: projectIds.map((projectId) => ({
      queryKey: taxonomyKeys.schema(projectId),
      queryFn: () => getTaxonomySchema(projectId),
      enabled: !!projectId,
      staleTime: 5 * 60 * 1000, // 5 minutes
    })),
  });

  // Build unified label maps from all schemas
  const fieldNames = new Map<string, string>();
  const optionLabels = new Map<string, string>();

  for (const result of results) {
    if (result.data?.fields) {
      for (const field of result.data.fields) {
        fieldNames.set(field.fieldId, field.name);
        for (const option of field.options) {
          optionLabels.set(option.optionId, option.label);
        }
      }
    }
  }

  return {
    fieldNames,
    optionLabels,
    isLoading: results.some((r) => r.isLoading),
  };
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Save or update a taxonomy schema
 */
export function useSaveTaxonomySchema(
  projectId: string
): UseMutationResult<TaxonomySchema, Error, Omit<TaxonomyField, 'createdAt'>[]> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fields: Omit<TaxonomyField, 'createdAt'>[]) => {
      const request: SaveTaxonomySchemaRequest = { fields };
      const schema = await saveTaxonomySchema(projectId, request);
      return schema;
    },
    onSuccess: (updatedSchema) => {
      // Update the taxonomy schema cache
      queryClient.setQueryData(
        taxonomyKeys.schema(projectId),
        updatedSchema
      );

      // Also invalidate the project detail to refresh taxonomySchema there
      queryClient.invalidateQueries({
        queryKey: projectsKeys.detail(projectId),
      });
    },
    onError: (error) => {
      console.error('Failed to save taxonomy schema:', error);
    },
  });
}

/**
 * Delete a taxonomy schema
 */
export function useDeleteTaxonomySchema(
  projectId: string
): UseMutationResult<void, Error, void> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await deleteTaxonomySchema(projectId);
    },
    onSuccess: () => {
      // Clear the taxonomy schema cache
      queryClient.setQueryData(taxonomyKeys.schema(projectId), null);

      // Also invalidate the project detail
      queryClient.invalidateQueries({
        queryKey: projectsKeys.detail(projectId),
      });
    },
    onError: (error) => {
      console.error('Failed to delete taxonomy schema:', error);
    },
  });
}
